import React, { useState } from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { Controller, DefaultValues, useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import { Maybe, UpdateNoteInput } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

type NoteItemProps = {
  id: string;
  date: Maybe<string> | undefined;
  text: Maybe<string> | undefined;
  onDeleteNote: () => void;
  onUpdateNote: () => Promise<unknown>;
};

const useStyles = makeStyles()(() => ({
  notesContainer: {
    borderBottom: "1px solid #D4DBE5",
    paddingBottom: "20px",
  },
  content: {
    wordWrap: "break-word",
    whiteSpace: "pre-wrap",
  },
  field: {
    marginBottom: 10,
  },
}));

const StyledCancelButton = withStyles(Button, {
  root: {
    marginTop: "12px",
    marginRight: "12px",
    backgroundColor: "#C0C5CD",
  },
});

const StyledSaveButton = withStyles(Button, {
  root: {
    marginTop: "12px",
    marginRight: "12px",
    backgroundColor: colors.primary,
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    color: colors.gray,
  },
});

const validationSchema = yup
  .object()
  .shape({
    date: yup.date().required("Please enter a valid date").nullable(),
    text: yup.string().required("Please enter your notes & observations"),
  })
  .required();

type FormValues = yup.InferType<typeof validationSchema>;

// important: do not delete
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const noteItemFragment = graphql(`
  fragment NoteItem on Note {
    id
    text
    date
    attachedTo
    createdAt
    updatedAt
  }
`);

const updateNoteMutation = graphql(`
  mutation UpdateNote($input: UpdateNoteInput!) {
    updateNote(input: $input) {
      note {
        ...NoteItem
      }
    }
  }
`);

const NoteItem: React.FC<NoteItemProps> = ({
  date,
  id,
  text,
  onDeleteNote,
  onUpdateNote,
}) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [updateNote] = useMutation(updateNoteMutation);
  const noteId = id;
  const initialValues: DefaultValues<FormValues> = {
    date: date ? new Date(date) : new Date(),
    text: text ?? "",
  };
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (values: FormValues) => {
    const editRemarksInput: UpdateNoteInput = {
      id: noteId,
      patch: {
        date: format(
          values.date ? new Date(values.date) : new Date(),
          "yyyy-MM-dd"
        ),
        text: values.text,
      },
    };
    try {
      await updateNote({ variables: { input: editRemarksInput } });
      snackbar.open("Successfully edited remark");
      setIsEditing(false);
      onUpdateNote();
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "An error occured while editing a remark. Please try again later."
        )
      );
    }
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <Grid item xs={12}>
      <div className={classes.notesContainer}>
        {isEditing ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={classes.field}>
              <Controller
                control={control}
                name="date"
                render={({
                  field: { ref, name, onChange, ...field },
                  fieldState,
                }) => (
                  <DatePicker
                    {...field}
                    inputRef={ref}
                    onChange={(value) => onChange(value as Date)}
                    slotProps={{
                      textField: {
                        name,
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </div>
            <StyledTextField
              {...register("text")}
              error={!!errors.text}
              helperText={errors.text?.message}
              disabled={!isEditing || isSubmitting}
              multiline
              variant="outlined"
              placeholder="Notes and observation"
              fullWidth
              color="primary"
            />
            <Box
              width="100%"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
            >
              <StyledCancelButton onClick={() => setIsEditing(false)}>
                Cancel
              </StyledCancelButton>
              <StyledSaveButton
                type="submit"
                disabled={isSubmitting}
                variant="contained"
              >
                Save
              </StyledSaveButton>
            </Box>
          </form>
        ) : (
          <>
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body1">
                {format(new Date(date ? date : new Date()), "dd MMM, yyyy")}
              </Typography>
              <IconButton onClick={handleOpen} size="large">
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleClose}
                TransitionComponent={Fade}
                PaperProps={{
                  elevation: 1,
                }}
                keepMounted
              >
                <MenuItem onClick={handleClose}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<EditIcon color="inherit" />}
                    onClick={() => setIsEditing(true)}
                  >
                    <Typography variant="body2" color="textPrimary">
                      Edit
                    </Typography>
                  </Button>
                </MenuItem>
                <MenuItem onClick={handleClose}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<DeleteForeverIcon color="inherit" />}
                    onClick={onDeleteNote}
                  >
                    <Typography variant="body2" color="textPrimary">
                      Delete
                    </Typography>
                  </Button>
                </MenuItem>
              </Menu>
            </Box>
            <Typography
              className={classes.content}
              variant="body2"
              color="textSecondary"
            >
              {text}
            </Typography>
          </>
        )}
      </div>
    </Grid>
  );
};

export default NoteItem;
